import { useCallback } from "react";
import { toNumber } from "lodash";

import { SeparableAccumulationItem, useListMemberSeparableAccumulationsQuery } from "@/App/api/member";
import { isFamilyCoverage } from "@/App/utils/familyCoverage";

const IN_NETWORK_STRINGS = ["in_network_tier_1"];

// the big four will have these types exactly
export const DEDUCTIBLE = "deductible";
export const OUT_OF_POCKET = "maximum_out_of_pocket";
export const PLAN_MAXIMUM = "plan_maximum";
export const FERTILITY = "plan_pay_fertility";

// custom accumulations also typed exactly
export const CUSTOM_STRING = "custom";
// legacy category accumulator types
export const CATEGORY_DEDUCTIBLE = "category_deductible_1";
export const CATEGORY_OUT_OF_POCKET = "out_of_pocket_category_1";

export const useAccumulations = () => {
  const { data: accumulations, isLoading: isLoadingAccumulations } = useListMemberSeparableAccumulationsQuery(true);

  const inNetworkAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>): Array<SeparableAccumulationItem> => {
    return accumulations?.filter((accum) => IN_NETWORK_STRINGS.includes(accum.network));
  }, []);

  const outOfNetworkAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>): Array<SeparableAccumulationItem> => {
    return accumulations?.filter((accum) => !IN_NETWORK_STRINGS.includes(accum.network));
  }, []);

  /*
  hi dev, some of the logic in the following two functions (individualAccumulations, familyAccumulations)
  can be a little difficult to follow so here is an English explanation:

  If an accumulator has a family_maximum and an individual_maximum,
  the accumulator will be displayed under both family and individual views IFF (if and only if) the accumulator is_embedded.

  If the accumulator has neither family_maximum NOR individual_maximum defined (they are null), this accumulator was
  fabricated in the api for the UI to display the default noAccumulator text (something like "There is no maximum...")
  and we want to pass it through as a family accumulator if the plan is a family plan and an
  individual accumulator if it is not a family plan.

  If the accumulator has an individual_maximum and not a family_maximum,
  we want to display this as an individual accumulator on family plans regardless of the is_embedded value.
  On individual plans, this accumulator is automatically passed through as an individual accumulator, no problem.

  If an accumulator has a family_maximum and no individual_maximum, that would be weird and
  I don't know what you do in either case. Probably call Charlotte.
*/

  const individualAccumulations = useCallback(
    (accumulations: Array<SeparableAccumulationItem>): Array<SeparableAccumulationItem> => {
      if (isFamilyCoverage) {
        // we only display individual accums on a family plan if they are embedded or if there is an individual max but no family max
        return accumulations?.filter((accum) => {
          const individualMaxAndNoFamilyMax = accum.family_maximum === null && Number(accum.individual_maximum) > 0;
          const isEmbedded = accum.is_embedded;
          return isEmbedded || individualMaxAndNoFamilyMax;
        });
      } else {
        return accumulations;
      }
    },
    [isFamilyCoverage],
  );

  const familyAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    if (isFamilyCoverage) {
      return accumulations?.filter((accum) => {
        const hasFamilyMax = Number(accum.family_maximum) > 0;
        const neitherFamilyOrIndividualMax = accum.individual_maximum === null && accum.family_maximum === null;
        return hasFamilyMax || neitherFamilyOrIndividualMax;
      });
    } else {
      return [];
    }
  }, []);

  const deductibleAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    return accumulations?.filter((accum) => accum.type === DEDUCTIBLE || accum.type === CATEGORY_DEDUCTIBLE);
  }, []);

  const outOfPocketAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    return accumulations?.filter((accum) => accum.type === OUT_OF_POCKET || accum.type === CATEGORY_OUT_OF_POCKET);
  }, []);

  const planMaxAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    return accumulations?.filter((accum) => accum.type === PLAN_MAXIMUM);
  }, []);

  const fertilityAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
      return accumulations?.filter((accum) => accum.type === FERTILITY);
  }, []);

  // since the ui displays these under one heading "Maximums" heres a function to return both
  const planMaximumAndFertilityAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    return accumulations?.filter((accum) => accum.type === FERTILITY || accum.type === PLAN_MAXIMUM);
  }, []);

  const customAccumulations = useCallback((accumulations: Array<SeparableAccumulationItem>) => {
    // All custom accumulations should have limits, but in case it somehow doesn't, don't show it
    return accumulations?.filter((accum) => accum.type === CUSTOM_STRING && toNumber(accum.family_maximum) > 0 && toNumber(accum.individual_maximum) > 0);
  }, []);

  return {
    accumulations: accumulations,
    isLoadingAccumulations: isLoadingAccumulations,
    inNetworkAccumulations: inNetworkAccumulations,
    outOfNetworkAccumulations: outOfNetworkAccumulations,
    individualAccumulations: individualAccumulations,
    familyAccumulations: familyAccumulations,
    deductibleAccumulations: deductibleAccumulations,
    outOfPocketAccumulations: outOfPocketAccumulations,
    planMaxAccumulations: planMaxAccumulations,
    fertilityAccumulations: fertilityAccumulations,
    planMaximumAndFertilityAccumulations: planMaximumAndFertilityAccumulations,
    customAccumulations: customAccumulations,
  };
};
